"use client";

import { useEffect, useId, useRef, useState } from "react";
import Link from "next/link";
import { ArrowRightIcon } from "@/components/icons";
import { CheckIcon } from "@/components/feature-icons";
import {
  INTEL_POINTS,
  PATHWAYS,
  RESULT_STATE_NOTE,
} from "@/app/features/person-intelligence-data";

const CENTRE = { x: 50, y: 52 };

function useInView<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setSeen(true);
          io.disconnect();
        }
      },
      { rootMargin: "0px 0px -10% 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return { ref, seen };
}

function DetailPanel({
  index,
  panelId,
}: {
  index: number;
  panelId: string;
}) {
  const p = INTEL_POINTS[index];
  const pathway = PATHWAYS.find((x) => x.id === p.pathway);
  return (
    <div
      id={panelId}
      role="region"
      aria-live="polite"
      className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-sm"
    >
      <p className="text-label-sm font-semibold uppercase tracking-[0.16em] text-brand-cyan">
        Point {p.num} of 12
      </p>
      <h3 className="mt-2 text-2xl font-bold text-white">{p.title}</h3>
      <p className="mt-3 text-sm leading-relaxed text-white/70">
        {p.description}
      </p>

      <p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.14em] text-white/40">
        Can include
      </p>
      <ul className="mt-2 grid gap-1.5 sm:grid-cols-2">
        {p.canInclude.map((item) => (
          <li key={item} className="flex items-start gap-2 text-[13px] text-white/75">
            <CheckIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-cyan" />
            {item}
          </li>
        ))}
      </ul>

      <p className="mt-5 flex items-start gap-2 border-t border-white/10 pt-4 text-sm font-medium text-white">
        <ArrowRightIcon className="mt-0.5 h-4 w-4 shrink-0 text-brand-cyan" aria-hidden />
        {p.benefit}
      </p>

      {pathway && (
        <Link
          href={`#${pathway.target}`}
          className="mt-4 inline-flex items-center gap-1.5 text-label-sm font-semibold text-brand-cyan hover:underline"
        >
          Opens into {pathway.label} research
          <ArrowRightIcon className="h-3.5 w-3.5" />
        </Link>
      )}
    </div>
  );
}

export function PersonIntelligence() {
  const { ref, seen } = useInView<HTMLDivElement>();
  const [selected, setSelected] = useState(0);
  const panelId = useId();
  const selPoint = INTEL_POINTS[selected];

  return (
    <section
      id="person-intelligence"
      className="scroll-mt-36 overflow-hidden bg-brand-navy px-container-padding py-section-py text-white"
    >
      <div className="mx-auto max-w-[1440px]">
        <div className="mx-auto mb-14 max-w-3xl text-center">
          <span className="mb-4 block text-label-sm font-semibold uppercase tracking-[0.2em] text-brand-cyan">
            KleipData · Person Intelligence
          </span>
          <h2 className="text-3xl font-bold tracking-tight md:text-4xl">
            12 intelligence areas.
            <br />
            One person view.
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-white/70">
            Instead of moving between separate searches, KleipData can bring
            supported person intelligence together first and present it as one
            consolidated research view.
          </p>
          <p className="mt-3 text-label-sm font-semibold uppercase tracking-[0.14em] text-white/40">
            12 intelligence areas · One KleipData request
          </p>
        </div>

        <div
          ref={ref}
          className="grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_400px]"
        >
          {/* ---------- Desktop constellation ---------- */}
          <div className="hidden lg:block">
            <div
              className={`pi-canvas relative mx-auto aspect-[7/6] w-full max-w-[720px] ${
                seen ? "is-in" : ""
              }`}
            >
              <svg
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
                className="absolute inset-0 h-full w-full"
                aria-hidden="true"
              >
                {INTEL_POINTS.map((p, i) => (
                  <line
                    key={p.id}
                    x1={CENTRE.x}
                    y1={CENTRE.y}
                    x2={p.pos.x}
                    y2={p.pos.y}
                    stroke={
                      i === selected
                        ? "rgba(0,217,217,0.9)"
                        : "rgba(255,255,255,0.14)"
                    }
                    strokeWidth={i === selected ? 0.5 : 0.35}
                    className={i === selected ? "flow-line" : undefined}
                  />
                ))}
              </svg>

              {/* Centre card */}
              <div className="absolute left-1/2 top-1/2 z-10 w-[150px] -translate-x-1/2 -translate-y-1/2 rounded-xl border border-brand-cyan/40 bg-brand-navy-2/90 p-3 text-center shadow-[0_0_40px_rgba(0,217,217,0.18)]">
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-brand-cyan">
                  Person Intelligence
                </p>
                <p className="mt-1 text-sm font-bold text-white">Alex Morgan</p>
                <p className="text-[10px] text-white/45">Illustrative record</p>
                <p className="mt-1.5 rounded bg-white/10 px-2 py-0.5 text-[10px] font-semibold text-white">
                  12-point view
                </p>
              </div>

              {/* Nodes */}
              {INTEL_POINTS.map((p, i) => {
                const Icon = p.icon;
                const active = i === selected;
                return (
                  <button
                    key={p.id}
                    type="button"
                    aria-pressed={active}
                    aria-controls={panelId}
                    onClick={() => setSelected(i)}
                    onMouseEnter={() => setSelected(i)}
                    style={{
                      left: `${p.pos.x}%`,
                      top: `${p.pos.y}%`,
                      animationDelay: `${i * 55}ms`,
                    }}
                    className={`pi-node absolute z-20 flex w-[112px] -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-1 rounded-xl border p-2 text-center transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-cyan ${
                      active
                        ? "border-brand-cyan/70 bg-brand-cyan/15"
                        : "border-white/10 bg-white/[0.03] hover:border-white/25"
                    }`}
                  >
                    <Icon
                      className={`h-5 w-5 ${active ? "text-brand-cyan" : "text-white/70"}`}
                    />
                    <span className="text-[11px] font-semibold leading-tight text-white">
                      {p.title}
                    </span>
                    <span className="text-[10px] leading-tight text-white/50">
                      {p.result}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ---------- Mobile / tablet: icon grid + accordion ---------- */}
          <div className="lg:hidden">
            <div className="rounded-xl border border-brand-cyan/40 bg-brand-navy-2/70 p-4 text-center">
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-brand-cyan">
                Person Intelligence
              </p>
              <p className="mt-1 text-base font-bold text-white">Alex Morgan</p>
              <p className="text-[11px] text-white/45">
                Illustrative record · 12-point view
              </p>
            </div>
            <ul className="mt-4 grid grid-cols-2 gap-2">
              {INTEL_POINTS.map((p, i) => {
                const Icon = p.icon;
                const open = i === selected;
                return (
                  <li
                    key={p.id}
                    className={i === selected ? "col-span-2 order-first" : ""}
                  >
                    <button
                      type="button"
                      aria-expanded={open}
                      aria-controls={`${panelId}-m-${i}`}
                      onClick={() => setSelected(open ? -1 : i)}
                      className={`flex w-full items-center gap-2 rounded-lg border p-3 text-left transition-colors ${
                        open
                          ? "border-brand-cyan/60 bg-brand-cyan/10"
                          : "border-white/10 bg-white/[0.03]"
                      }`}
                    >
                      <Icon
                        className={`h-5 w-5 shrink-0 ${open ? "text-brand-cyan" : "text-white/70"}`}
                      />
                      <span className="min-w-0">
                        <span className="block text-[12px] font-semibold leading-tight text-white">
                          {p.title}
                        </span>
                        <span className="block text-[10px] text-white/50">
                          {p.result}
                        </span>
                      </span>
                    </button>
                    {open && (
                      <div id={`${panelId}-m-${i}`} className="mt-2">
                        <DetailPanel index={i} panelId={`${panelId}-mp-${i}`} />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          {/* ---------- Desktop detail panel ---------- */}
          <div className="hidden lg:block lg:sticky lg:top-32">
            <DetailPanel index={selected < 0 ? 0 : selected} panelId={panelId} />
          </div>
        </div>

        {/* Illustrative-data note + area list (semantic fallback) */}
        <p className="mx-auto mt-10 max-w-3xl text-center text-xs leading-relaxed text-white/45">
          {RESULT_STATE_NOTE}
        </p>

        <div className="mx-auto mt-8 max-w-3xl">
          <p className="text-center text-[11px] font-semibold uppercase tracking-[0.14em] text-white/40">
            The 12 intelligence areas
          </p>
          <ol className="mt-3 flex flex-wrap justify-center gap-x-3 gap-y-1.5 text-[13px] text-white/60">
            {INTEL_POINTS.map((p) => (
              <li key={p.id} className="flex items-center gap-1.5">
                <span className="text-white/30">{p.num}.</span>
                <button
                  type="button"
                  onClick={() => setSelected(INTEL_POINTS.indexOf(p))}
                  className="hover:text-brand-cyan hover:underline"
                >
                  {p.title}
                </button>
              </li>
            ))}
          </ol>
        </div>

        <p className="mt-8 text-center text-sm text-white/60">
          Currently showing:{" "}
          <span className="font-semibold text-white">{selPoint?.title}</span>
        </p>
      </div>
    </section>
  );
}
